/**
 * FormFieldRenderer
 * Renders a single field based on type (text, date, dropdown, file, ranking, etc.)
 */

import { LocationAutocomplete } from "@/components/ui/create-event/LocationAutocomplete";
import { FetchDropdown } from "@/fetch-ui/FetchDropdown";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { FetchTextArea } from "@/fetch-ui/FetchTextArea";
import { FormField, RICH_TEXT_CONTENT_FIELD_TYPE } from "@/lib/database/forms";
import { pickAddressAnswer, savedAddressLocations } from "@/features/forms/_logic/address-answer";
import type { SignatureValue } from "@/features/forms/_logic/types/file-mapping-types";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import DOMPurify from "dompurify";
import { Star } from "lucide-react";
import { memo, useMemo } from "react";
import { FormPhoneField } from "./FormPhoneField";
import { SignatureInput } from "./SignatureInput";
import { ContentBlockPreview } from "../shared/ContentBlockPreview";

/**
 * Sanitizes description HTML to allow only safe formatting tags and link attributes. Browser only:
 * plain DOMPurify cannot sanitize without a DOM.
 * @param html - The field description as stored.
 * @returns The description with everything outside the allowlist removed.
 */
export const sanitizeDescriptionHtml = (html: string): string =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["b", "strong", "em", "i", "br", "a", "span", "p"],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });

export const HtmlDescription = memo(({ html }: { html: string }) => {
  const sanitizedHtml = useMemo(() => sanitizeDescriptionHtml(html), [html]);
  return (
    <div
      className="text-sm text-gray-600 mt-1 mb-3 [&_a]:text-blue-600 [&_a]:underline"
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
});
HtmlDescription.displayName = "HtmlDescription";

interface FormFieldRendererProps {
  field: FormField;
  value: any;
  onChange?: (value: any) => void;
  onFileUpload?: (file: File) => void;
  isUploading?: boolean;
  disabled?: boolean;
  showValidationErrors?: boolean;
  hideDescription?: boolean;
  /** Form ID for file uploads (signature, etc.). */
  formId?: string;
  /** Resolved minimum date (ISO `YYYY-MM-DD`) for a dependent `date` field. Applied as the native `min`. */
  dateMin?: string;
  /** Resolved maximum date (ISO `YYYY-MM-DD`) for a dependent `date` field. Applied as the native `max`. */
  dateMax?: string;
}

// Special constant for "Other" option value
const OTHER_OPTION_VALUE = "__OTHER__";
const OTHER_OPTION_LABEL = "Other";

/** Infers a browser autocomplete token from a freeform field label and type. */
const getAutoCompleteToken = (field: FormField): React.InputHTMLAttributes<HTMLInputElement>["autoComplete"] => {
  const plainLabel = field.label
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

  if (field.type === "email" || plainLabel === "email" || plainLabel.includes("email address")) {
    return "email";
  }

  if (field.type === "tel" || plainLabel.includes("phone") || plainLabel.includes("mobile")) {
    return "tel";
  }

  if (field.type === "address") {
    return "street-address";
  }

  if (plainLabel.includes("first name") || plainLabel === "first" || plainLabel === "given name") {
    return "given-name";
  }

  if (plainLabel.includes("last name") || plainLabel === "last" || plainLabel === "surname") {
    return "family-name";
  }

  if (plainLabel === "full name" || plainLabel === "name" || plainLabel.includes("your name")) {
    return "name";
  }

  if (plainLabel.includes("address line 1")) {
    return "address-line1";
  }

  if (plainLabel.includes("address line 2") || plainLabel.includes("apt") || plainLabel.includes("suite")) {
    return "address-line2";
  }

  if (plainLabel === "address" || plainLabel.includes("street address")) {
    return "street-address";
  }

  if (plainLabel === "city" || plainLabel.includes("town")) {
    return "address-level2";
  }

  if (plainLabel === "state" || plainLabel.includes("province") || plainLabel.includes("region")) {
    return "address-level1";
  }

  if (plainLabel.includes("zip") || plainLabel.includes("postal code") || plainLabel.includes("postcode")) {
    return "postal-code";
  }

  if (plainLabel === "country") {
    return "country-name";
  }

  return undefined;
};

export default function FormFieldRenderer({
  field,
  value,
  onChange,
  onFileUpload,
  isUploading = false,
  disabled = false,
  showValidationErrors = false,
  hideDescription = false,
  formId,
  dateMin,
  dateMax,
}: FormFieldRendererProps) {
  // A content block renders formatted text in place of a question — no label, input,
  // or validation state applies to it.
  if (field.type === RICH_TEXT_CONTENT_FIELD_TYPE) {
    return <ContentBlockPreview html={field.content ?? ""} />;
  }

  const isInvalid =
    showValidationErrors &&
    field.is_required &&
    (!value ||
      (typeof value === "string" && value.trim() === "") ||
      (typeof value === "object" && value !== null && "phone" in value && (!value.phone || value.phone.trim() === "")));

  // Announced, non-color error cue (WCAG 1.4.1 / 3.3.1 / 4.1.3) — rendered under
  // the control for every field type so the error isn't conveyed by color alone.
  // The id lets controls link to it via aria-describedby.
  const errorId = `field-error-${field.id}`;
  const describedBy = isInvalid ? errorId : undefined;
  const requiredAlert = isInvalid ? (
    <p id={errorId} role="alert" className="mt-1 text-xs font-medium text-red-600">
      This field is required.
    </p>
  ) : null;

  // Helper to get choices with optional "Other" added
  const getChoicesWithOther = (choices?: string[]): string[] => {
    const baseChoices = choices || ["Yes", "No"];
    if (field.has_other_option) {
      return [...baseChoices, OTHER_OPTION_VALUE];
    }
    return baseChoices;
  };

  // Helper to check if "Other" is selected
  const isOtherSelected = (): boolean => {
    if (field.type === "dropdown" || field.type === "multiplechoice") {
      return value === OTHER_OPTION_VALUE || (typeof value === "object" && value?.value === OTHER_OPTION_VALUE);
    }
    if (field.type === "multiselect") {
      return Array.isArray(value) && value.some((v) => v === OTHER_OPTION_VALUE || v?.value === OTHER_OPTION_VALUE);
    }
    return false;
  };

  // Helper to get the "Other" text value
  const getOtherText = (): string => {
    if (typeof value === "object" && value?.otherText) {
      return value.otherText;
    }
    if (Array.isArray(value)) {
      const otherItem = value.find((v) => typeof v === "object" && v?.otherText);
      return otherItem?.otherText || "";
    }
    return "";
  };

  const renderDescription = () => {
    if (hideDescription || !field.description) return null;
    return <HtmlDescription html={field.description} />;
  };

  const handleMultiSelectChange = (choice: string) => {
    if (!onChange || disabled) return;
    const currentValues = Array.isArray(value) ? [...value] : [];

    if (choice === OTHER_OPTION_VALUE) {
      // Toggle "Other" option
      const hasOther = currentValues.some(
        (v) => v === OTHER_OPTION_VALUE || (typeof v === "object" && v?.value === OTHER_OPTION_VALUE)
      );
      if (hasOther) {
        // Remove "Other"
        onChange(
          currentValues.filter(
            (v) => !(v === OTHER_OPTION_VALUE || (typeof v === "object" && v?.value === OTHER_OPTION_VALUE))
          )
        );
      } else {
        // Add "Other" with empty text
        onChange([...currentValues, { value: OTHER_OPTION_VALUE, otherText: "" }]);
      }
    } else {
      // Normal choice handling
      if (currentValues.includes(choice)) {
        onChange(currentValues.filter((v) => v !== choice));
      } else {
        onChange([...currentValues, choice]);
      }
    }
  };

  // File Upload
  if (field.type === "file") {
    if (disabled) {
      return (
        <>
          {renderDescription()}
          <input type="file" className="file-input file-input-bordered w-full touch-manipulation" disabled />
        </>
      );
    }
    return (
      <div>
        {renderDescription()}
        <input
          type="file"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.webp"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && onFileUpload) {
              onFileUpload(file);
            }
          }}
          className="file-input file-input-bordered w-full touch-manipulation"
          required={field.is_required && !value}
        />
        {value && (
          <div className="text-sm text-gray-600 mt-1">
            {isUploading ? (
              "Uploading..."
            ) : (
              <>
                File uploaded: {value.split("/").pop()}
                <div className="text-xs text-gray-400">Path: {value}</div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // Helper to extract plain text from markdown links for dropdown display
  const stripMarkdownLinks = (text: string): string => {
    // Remove markdown links but keep the link text
    return text.replace(/\[([^\]]+)\]\s*\(\s*https?:\/\/[^)\s]+\s*\)/g, "$1");
  };

  // Dropdown
  if (field.type === "dropdown") {
    // Extract the actual value (could be a string or an object with {value, otherText})
    const actualValue = typeof value === "object" && value !== null ? value.value : value;
    const displayValue = actualValue || "";
    const showOtherInput = displayValue === OTHER_OPTION_VALUE;

    return (
      <>
        {renderDescription()}
        <div className="space-y-3">
          <FetchDropdown
            value={displayValue}
            onChange={(e) => {
              const selectedValue = e.target.value;
              if (selectedValue === OTHER_OPTION_VALUE) {
                onChange?.({ value: OTHER_OPTION_VALUE, otherText: "" });
              } else {
                onChange?.(selectedValue);
              }
            }}
            options={getChoicesWithOther(field.choices).map((choice: string) => ({
              value: choice,
              label: choice === OTHER_OPTION_VALUE ? OTHER_OPTION_LABEL : stripMarkdownLinks(choice),
            }))}
            placeholder="Select an option"
            required={field.is_required}
            disabled={disabled}
            status={isInvalid ? "error" : "default"}
            clearable={!disabled}
          />

          {/* Show text input when "Other" is selected */}
          {showOtherInput && !disabled && (
            <div className="pl-4 border-l-2 border-gray-300">
              <label className="block text-sm text-gray-600 mb-1">Please specify:</label>
              <FetchInput
                type="text"
                value={getOtherText()}
                onChange={(e) => {
                  onChange?.({ value: OTHER_OPTION_VALUE, otherText: e.target.value });
                }}
                placeholder="Type your answer..."
                required={field.is_required}
              />
            </div>
          )}
        </div>
        {requiredAlert}
      </>
    );
  }

  // Helper to convert markdown links and URLs in text to clickable links
  const convertUrlsToLinks = (text: string): (string | JSX.Element)[] => {
    // First, handle markdown-style links [text](url)
    const markdownLinkRegex = /\[([^\]]+)\]\s*\(\s*(https?:\/\/[^)\s]+)\s*\)/g;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;
    let keyCounter = 0;

    while ((match = markdownLinkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        const beforeText = text.slice(lastIndex, match.index);
        parts.push(...convertRawUrls(beforeText, keyCounter));
        keyCounter += beforeText.split(/(https?:\/\/[^\s]+)/g).length;
      }

      // Add the markdown link
      parts.push(
        <a
          key={`md-${keyCounter++}`}
          href={match[2].trim()}
          target="_blank"
          rel="noopener noreferrer"
          className="touch-manipulation text-blue-600 [@media(hover:hover)]:hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {match[1]}
        </a>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      parts.push(...convertRawUrls(remainingText, keyCounter));
    } else if (lastIndex === 0) {
      parts.push(...convertRawUrls(text, keyCounter));
    }

    return parts;
  };

  // Helper to convert raw URLs in text
  const convertRawUrls = (text: string, startKey: number = 0): (string | JSX.Element)[] => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, i) =>
      urlRegex.test(part) ? (
        <a
          key={`url-${startKey + i}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="touch-manipulation text-blue-600 [@media(hover:hover)]:hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      ) : (
        part
      )
    );
  };

  // Multiple Choice (radio buttons)
  if (field.type === "multiplechoice") {
    // Extract the actual value (could be a string or an object with {value, otherText})
    const actualValue = typeof value === "object" && value !== null ? value.value : value;
    const displayValue = actualValue || "";
    const showOtherInput = displayValue === OTHER_OPTION_VALUE;

    return (
      <>
        {renderDescription()}
        <div className="space-y-3">
          <div className="space-y-2">
            {getChoicesWithOther(field.choices).map((choice: string, idx: number) => {
              const isChecked = displayValue === choice;
              const displayText = choice === OTHER_OPTION_VALUE ? OTHER_OPTION_LABEL : choice;

              return (
                <label key={idx} className="flex touch-manipulation cursor-pointer items-center space-x-2">
                  <input
                    type="radio"
                    name={`field-${field.id}`}
                    checked={isChecked}
                    onChange={() => {
                      if (choice === OTHER_OPTION_VALUE) {
                        onChange?.({ value: OTHER_OPTION_VALUE, otherText: "" });
                      } else {
                        onChange?.(choice);
                      }
                    }}
                    disabled={disabled}
                    className="radio"
                    required={field.is_required && !displayValue}
                  />
                  <span className={isInvalid ? "text-red-600" : ""}>{convertUrlsToLinks(displayText)}</span>
                </label>
              );
            })}
          </div>

          {/* Show text input when "Other" is selected */}
          {showOtherInput && !disabled && (
            <div className="pl-4 border-l-2 border-gray-300 ml-6">
              <label className="block text-sm text-gray-600 mb-1">Please specify:</label>
              <FetchInput
                type="text"
                value={getOtherText()}
                onChange={(e) => {
                  onChange?.({ value: OTHER_OPTION_VALUE, otherText: e.target.value });
                }}
                placeholder="Type your answer..."
                required={field.is_required}
              />
            </div>
          )}
        </div>
        {requiredAlert}
      </>
    );
  }

  // Multi-select
  if (field.type === "multiselect") {
    const currentValues = Array.isArray(value) ? value : [];
    const isOtherChecked = currentValues.some(
      (v) => v === OTHER_OPTION_VALUE || (typeof v === "object" && v?.value === OTHER_OPTION_VALUE)
    );

    return (
      <>
        {renderDescription()}
        <div className="space-y-3">
          <div className="space-y-2">
            {getChoicesWithOther(field.choices).map((choice: string, idx: number) => {
              const isChecked = choice === OTHER_OPTION_VALUE ? isOtherChecked : currentValues.includes(choice);
              const displayText = choice === OTHER_OPTION_VALUE ? OTHER_OPTION_LABEL : choice;

              return (
                <label key={idx} className="flex touch-manipulation cursor-pointer items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleMultiSelectChange(choice)}
                    disabled={disabled}
                    className="checkbox"
                  />
                  <span className={isInvalid ? "text-red-600" : ""}>{convertUrlsToLinks(displayText)}</span>
                </label>
              );
            })}
          </div>

          {/* Show text input when "Other" is checked */}
          {isOtherChecked && !disabled && (
            <div className="pl-4 border-l-2 border-gray-300 ml-6">
              <label className="block text-sm text-gray-600 mb-1">Please specify:</label>
              <FetchInput
                type="text"
                value={getOtherText()}
                onChange={(e) => {
                  const newValues = currentValues.map((v) => {
                    if (v === OTHER_OPTION_VALUE || (typeof v === "object" && v?.value === OTHER_OPTION_VALUE)) {
                      return { value: OTHER_OPTION_VALUE, otherText: e.target.value };
                    }
                    return v;
                  });
                  onChange?.(newValues);
                }}
                placeholder="Type your answer..."
              />
            </div>
          )}
        </div>
        {requiredAlert}
      </>
    );
  }

  // Ranking
  if (field.type === "ranking") {
    if (disabled) {
      return (
        <div>
          {renderDescription()}
          <div className="space-y-2">
            <div className="text-sm text-gray-500 italic mb-2">Drag to rank (preview)</div>
            {(value || field.choices || []).map((item: string, idx: number) => (
              <div
                key={idx}
                className="p-3 border border-gray-300 rounded-lg flex items-center justify-between bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-500">#{idx + 1}</span>
                  <span className="text-sm">{convertUrlsToLinks(item)}</span>
                </div>
                <span className="text-gray-400">⋮⋮</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Inline RankingField component
    const items = (value as string[]) || field.choices || [];
    const handleDragEnd = (result: any) => {
      if (!result.destination || !onChange) return;

      const reorderedItems = Array.from(items);
      const [movedItem] = reorderedItems.splice(result.source.index, 1);
      reorderedItems.splice(result.destination.index, 0, movedItem);

      onChange(reorderedItems);
    };

    return (
      <div>
        {renderDescription()}
        <div className="space-y-2">
          <div className="text-sm text-gray-600 mb-2">Drag items to rank them (top = most preferred)</div>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={`ranking-${field.id}`}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                  {items.map((item: string, idx: number) => (
                    <Draggable key={`${field.id}-${item}`} draggableId={`${field.id}-${item}`} index={idx}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`p-3 border rounded-lg flex items-center justify-between cursor-grab ${
                            snapshot.isDragging ? "bg-blue-50 border-blue-300 shadow-lg" : "bg-white border-gray-300"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-gray-500">#{idx + 1}</span>
                            <span className="text-sm">{convertUrlsToLinks(item)}</span>
                          </div>
                          <span className="text-gray-400">⋮⋮</span>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    );
  }

  // URL
  if (field.type === "url") {
    return (
      <>
        {renderDescription()}
        <FetchInput
          type="url"
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          autoComplete={getAutoCompleteToken(field)}
          disabled={disabled}
          error={isInvalid}
        />
      </>
    );
  }

  // Address
  if (field.type === "address") {
    const addressValue = value || "";
    const selectedAddresses = addressValue ? [addressValue] : [];

    return (
      <>
        {renderDescription()}
        <LocationAutocomplete
          searchType="address"
          selectedLocations={selectedAddresses}
          onLocationChange={(addresses, locations) => {
            onChange?.(pickAddressAnswer(addresses, locations));
          }}
          formattedLocations={savedAddressLocations(addressValue)}
          disabled={disabled}
          placeholder="Search for an address..."
          limit={1}
        />
      </>
    );
  }

  // Phone / Tel field with country code
  if (field.type === "tel") {
    return (
      <>
        {renderDescription()}
        <FormPhoneField
          value={value}
          onChange={onChange}
          disabled={disabled}
          isInvalid={isInvalid}
          placeholder={field.label}
        />
      </>
    );
  }

  // Signature field
  if (field.type === "signature") {
    return (
      <>
        {renderDescription()}
        <SignatureInput
          value={value as SignatureValue | undefined}
          onChange={(val) => onChange?.(val)}
          required={field.is_required}
          formId={formId || ""}
          fieldId={field.id}
          disabled={disabled}
        />
      </>
    );
  }

  // Scale / numeric rating (e.g. 1-10, NPS 0-10, 1-5 stars).
  // The answer is a plain number, or `null` when the optional "N/A" choice is picked.
  if (field.type === "scale") {
    const cfg = field.scale_config || { min: 1, max: 5 };
    const min = Number.isFinite(cfg.min) ? cfg.min : 1;
    const max = Number.isFinite(cfg.max) ? cfg.max : 5;
    const options = max >= min ? Array.from({ length: max - min + 1 }, (_, i) => min + i) : [];
    const current = typeof value === "number" ? value : null;
    const isNA = value === null;
    const useStars = cfg.display === "stars";
    // value === 0 is a valid answer (e.g. NPS); "" is the un-answered initial state.
    const scaleInvalid =
      showValidationErrors && field.is_required && (value === undefined || value === null || value === "");

    return (
      <>
        {renderDescription()}
        <div className="space-y-2">
          <div
            className="flex flex-wrap items-center gap-2"
            role="group"
            aria-label={field.label}
            aria-invalid={scaleInvalid || undefined}
            aria-describedby={scaleInvalid ? errorId : undefined}
          >
            {options.map((option) => {
              const selected = current === option;
              if (useStars) {
                const filled = current != null && option <= current;
                return (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={selected}
                    aria-label={`${option} star${option === 1 ? "" : "s"}`}
                    onClick={() => onChange?.(option)}
                    disabled={disabled}
                    className={`flex h-11 w-11 touch-manipulation items-center justify-center transition ${
                      disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                    }`}
                  >
                    <Star className={`h-7 w-7 ${filled ? "fill-indigo-500 text-indigo-500" : "text-gray-300"}`} />
                  </button>
                );
              }
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onChange?.(option)}
                  disabled={disabled}
                  className={`min-w-[2.5rem] rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                    selected
                      ? "border-indigo-600 bg-indigo-600 text-white"
                      : "border-gray-300 bg-white text-gray-700 [@media(hover:hover)]:hover:border-indigo-400"
                  } touch-manipulation ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                >
                  {option}
                </button>
              );
            })}
            {!field.is_required && (
              <button
                type="button"
                onClick={() => onChange?.(isNA ? undefined : null)}
                disabled={disabled}
                aria-pressed={isNA}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  isNA
                    ? "border-gray-600 bg-gray-600 text-white"
                    : "border-gray-300 bg-white text-gray-600 [@media(hover:hover)]:hover:border-gray-400"
                } touch-manipulation ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
              >
                {cfg.na_label || "N/A"}
              </button>
            )}
          </div>
          {(cfg.min_label || cfg.max_label) && (
            <div className="flex justify-between text-xs font-medium text-gray-500">
              <span>{cfg.min_label}</span>
              <span>{cfg.max_label}</span>
            </div>
          )}
          {scaleInvalid && (
            <p id={errorId} role="alert" className="text-xs font-medium text-red-600">
              This field is required.
            </p>
          )}
        </div>
      </>
    );
  }

  // Helper to map field.type to valid FetchInput type
  const getInputType = ():
    | "text"
    | "number"
    | "password"
    | "email"
    | "tel"
    | "url"
    | "currency"
    | "date"
    | "datetime-local"
    | "time"
    | undefined => {
    type FetchInputType =
      | "text"
      | "number"
      | "password"
      | "email"
      | "tel"
      | "url"
      | "currency"
      | "date"
      | "datetime-local"
      | "time";
    const validTypes: FetchInputType[] = [
      "text",
      "number",
      "password",
      "email",
      "tel",
      "url",
      "currency",
      "date",
      "datetime-local",
      "time",
    ];
    return (validTypes.includes(field.type as FetchInputType) ? field.type : "text") as FetchInputType;
  };

  // Date fields can depend on another date field (e.g. departure on/after arrival).
  // Bounds are inclusive, matching native date input min/max semantics. A filled
  // date outside its bounds is flagged so the field shows an error state, not just
  // a silent submit block.
  const isDate = field.type === "date";
  const isDateOutOfRange =
    isDate &&
    typeof value === "string" &&
    value.trim() !== "" &&
    Boolean((dateMin && value < dateMin) || (dateMax && value > dateMax));

  // Character-limited text fields show a live counter and flag over-length input.
  const isTextType = getInputType() === "text";
  const overMaxLength = Boolean(
    field.max_length && isTextType && typeof value === "string" && value.length > field.max_length
  );

  const charCounter =
    field.max_length && isTextType ? (
      <p className={`mt-1 text-right text-xs font-medium ${overMaxLength ? "text-red-600" : "text-gray-500"}`}>
        {typeof value === "string" ? value.length : 0} / {field.max_length}
      </p>
    ) : null;

  // Multi-line text fields render a bigger, user-resizable textarea (drag the
  // bottom-right handle); rows={4} is the starting height.
  if (field.multiline && isTextType) {
    return (
      <>
        {renderDescription()}
        <FetchTextArea
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={disabled ? "" : field.label}
          disabled={disabled}
          error={isInvalid || overMaxLength}
          aria-invalid={isInvalid || undefined}
          aria-describedby={describedBy}
          maxLength={field.max_length}
          rows={4}
          resizable
        />
        {charCounter}
        {requiredAlert}
      </>
    );
  }

  // Default (text, date, time, number, email, etc.)
  return (
    <>
      {renderDescription()}
      <FetchInput
        type={getInputType()}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={disabled ? "" : field.label}
        autoComplete={getAutoCompleteToken(field)}
        disabled={disabled}
        error={isInvalid || (showValidationErrors && isDateOutOfRange) || overMaxLength}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy}
        min={isDate ? dateMin : undefined}
        max={isDate ? dateMax : undefined}
        maxLength={field.max_length && isTextType ? field.max_length : undefined}
      />
      {charCounter}
      {requiredAlert}
    </>
  );
}
